import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { wsService } from '@/services/websocket';
import { webrtcService } from '@/services/webrtc';
import { Topic } from '@/services/api';
import { AdInterstitial } from '@/modules/ads';

type DebatePhase = 
  | 'preparing'     // 토론자 준비 대기 + 배심원 모집
  | 'countdown'     // 시작 카운트다운
  | 'pro_main'      // 찬성측 주발언 (60초)
  | 'con_main'      // 반대측 주발언 (60초)
  | 'pro_rebuttal'  // 찬성측 반론 (30초)
  | 'con_rebuttal'  // 반대측 반론 (30초)
  | 'voting'        // 배심원 투표
  | 'result';       // 결과 발표

type Role = 'debater' | 'jury' | 'spectator';
type Stance = 'pro' | 'con';

interface LocationState {
  role?: Role;
  topicId?: string;
  stance?: Stance;
  topic?: Topic;
}

// 토론 타임라인
const PHASE_DURATIONS: Record<string, number> = {
  countdown: 3,
  pro_main: 60,
  con_main: 60,
  pro_rebuttal: 30,
  con_rebuttal: 30,
  voting: 30,
};

const PHASE_ORDER: DebatePhase[] = [
  'countdown',
  'pro_main',
  'con_main',
  'pro_rebuttal',
  'con_rebuttal',
  'voting',
  'result',
];

export default function DebateRoomPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;

  const [role, setRole] = useState<Role>(locationState?.role || 'spectator');
  const [myStance] = useState<Stance | undefined>(locationState?.stance);
  const [topic] = useState<Topic | undefined>(locationState?.topic);

  // 토론 상태
  const [phase, setPhase] = useState<DebatePhase>('preparing');
  const [timeLeft, setTimeLeft] = useState(0);
  const [proReady, setProReady] = useState(false);
  const [conReady, setConReady] = useState(false);

  // 배심원 대기 상태
  const [juryWaitDeadline, setJuryWaitDeadline] = useState<number | null>(null);
  const [juryWaitTimeLeft, setJuryWaitTimeLeft] = useState(0);
  
  // 양쪽 준비 후 시작 대기
  const [startDeadline, setStartDeadline] = useState<number | null>(null);
  const [startTimeLeft, setStartTimeLeft] = useState(0);

  // 배심원/투표
  const [juryCount, setJuryCount] = useState(0);
  const [myVote, setMyVote] = useState<Stance | 'draw' | 'abstain' | null>(null);
  const [votes, setVotes] = useState({ pro: 0, con: 0, draw: 0, abstain: 0 });
  const [totalVotes, setTotalVotes] = useState(0);

  // 배심원 실시간 지지
  const [mySupport, setMySupport] = useState<Stance | null>(null);
  const [supportCounts, setSupportCounts] = useState({ pro: 0, con: 0, neutral: 0 });

  // 음성 관련
  const [isMicReady, setIsMicReady] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [micError, setMicError] = useState<string | null>(null);
  const [connectedPeers, setConnectedPeers] = useState(0);

  // 리액션 표시
  const [recentReactions, setRecentReactions] = useState<{id: string; emoji: string; target: string}[]>([]);

  // 결과
  const [winner, setWinner] = useState<Stance | 'draw' | null>(null);
  const [resultCountdown, setResultCountdown] = useState(10);
  const [showInterstitialAd, setShowInterstitialAd] = useState(false);

  // 현재 발언권
  const currentSpeaker: Stance | null = 
    phase === 'pro_main' || phase === 'pro_rebuttal' ? 'pro' :
    phase === 'con_main' || phase === 'con_rebuttal' ? 'con' : null;

  // 내가 발언할 차례인지
  const isMyTurn = role === 'debater' && currentSpeaker === myStance;

  // 다음 페이즈로 이동
  const goToNextPhase = useCallback(() => {
    const currentIndex = PHASE_ORDER.indexOf(phase);
    if (currentIndex < PHASE_ORDER.length - 1) {
      const nextPhase = PHASE_ORDER[currentIndex + 1];
      setPhase(nextPhase);
      setTimeLeft(PHASE_DURATIONS[nextPhase] || 0);
      
      // 서버에 페이즈 변경 알림 (result는 서버에서 finished로 처리)
      const serverPhase = nextPhase === 'result' ? 'finished' : nextPhase;
      wsService.send({ action: 'CHANGE_PHASE', phase: serverPhase });
    }
  }, [phase]);

  const wakeLockRef = useRef<WakeLockSentinel | null>(null);

  // 화면 꺼짐 방지 (Wake Lock API)
  useEffect(() => {
    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
          console.log('Wake Lock activated');
        }
      } catch (err) {
        console.log('Wake Lock not supported or failed:', err);
      }
    };

    requestWakeLock();

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && !wakeLockRef.current) {
        await requestWakeLock();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    };
  }, []);

  // WebSocket 연결 및 방 참가
  useEffect(() => {
    async function joinRoom() {
      try {
        await wsService.connect();
        wsService.send({ action: 'JOIN_ROOM' });
      } catch (err) {
        console.error('Failed to join room:', err);
      }
    }

    joinRoom();

    return () => {
      wsService.send({ action: 'LEAVE_ROOM' });
    };
  }, []);

  // WebRTC 초기화 - 역할이 확정된 후 실행
  const [webrtcInitialized, setWebrtcInitialized] = useState(false);
  
  useEffect(() => {
    // 역할이 확정되지 않았거나 이미 초기화됐으면 스킵
    if (!role || webrtcInitialized) return;
    
    async function initWebRTC() {
      try {
        if (role === 'debater') {
          await webrtcService.initialize();
          setIsMicReady(true);
          // 방 참가자 목록 요청
          webrtcService.requestParticipants();
        } else {
          // 배심원/관전자는 마이크 없이 청취자로 초기화
          await webrtcService.initializeAsListener();
        }
        setWebrtcInitialized(true);
      } catch (err) {
        console.error('WebRTC init failed:', err);
        setMicError('마이크 접근 권한이 필요합니다.');
      }
    }

    // 약간의 딜레이 후 초기화 (서버 연결이 완료되도록)
    const timer = setTimeout(() => {
      initWebRTC();
    }, 500);

    // WebRTC 이벤트 리스너
    const unsubPeerConnected = webrtcService.on('peerConnected', () => {
      setConnectedPeers(webrtcService.getConnectionCount());
    });

    const unsubPeerDisconnected = webrtcService.on('peerDisconnected', () => {
      setConnectedPeers(webrtcService.getConnectionCount());
    });

    const unsubRemoteStream = webrtcService.on('remoteStreamAdded', () => {
      // 피어 수 업데이트
      setConnectedPeers(webrtcService.getConnectionCount());
    });

    const unsubError = webrtcService.on('error', (data) => {
      const errorData = data as { message: string };
      setMicError(errorData.message);
    });

    return () => {
      clearTimeout(timer);
      unsubPeerConnected();
      unsubPeerDisconnected();
      unsubRemoteStream();
      unsubError();
    };
  }, [role, webrtcInitialized]);
  
  // cleanup은 별도로
  useEffect(() => {
    return () => {
      webrtcService.cleanup();
    };
  }, []);

  // 발언권에 따라 음소거 자동 제어
  useEffect(() => {
    if (role === 'debater') {
      // 내 차례일 때만 마이크 켜기
      const shouldUnmute = isMyTurn;
      webrtcService.setMuted(!shouldUnmute);
      setIsMuted(!shouldUnmute);
    }
  }, [role, isMyTurn]);

  // WebSocket 메시지 핸들러
  useEffect(() => {
    // 재연결 (이미 참가 중인 경우)
    const unsubRejoined = wsService.on('ROOM_REJOINED', (data) => {
      console.log('Room rejoined:', data);
      setRole(data.role);
      if (data.stance) {
        // myStance는 useState로 관리되므로 직접 변경 불가
      }
      setPhase(data.debate?.status as DebatePhase || 'preparing');
      setJuryCount(data.debate?.juryCount || 0);
      setProReady(data.debate?.proReady || false);
      setConReady(data.debate?.conReady || false);
      setJuryWaitDeadline(data.debate?.juryWaitDeadline || null);
      setSupportCounts(data.debate?.support || { pro: 0, con: 0, neutral: 0 });
    });

    // 배심원으로 참가
    const unsubJoinedJury = wsService.on('JOINED_AS_JURY', (data) => {
      console.log('Joined as jury:', data);
      setRole('jury');
      setPhase(data.debate?.status as DebatePhase || 'preparing');
      setJuryCount(data.debate?.juryCount || 0);
      setProReady(data.debate?.proReady || false);
      setConReady(data.debate?.conReady || false);
      setJuryWaitDeadline(data.debate?.juryWaitDeadline || null);
    });

    // 관전자로 참가
    const unsubJoinedSpectator = wsService.on('JOINED_AS_SPECTATOR', (data) => {
      console.log('Joined as spectator:', data);
      setRole('spectator');
      setPhase(data.debate?.status as DebatePhase || 'preparing');
      setJuryCount(data.debate?.juryCount || 0);
      setSupportCounts(data.debate?.support || { pro: 0, con: 0, neutral: 0 });
    });

    const unsubJuryJoined = wsService.on('JURY_JOINED', (data) => {
      setJuryCount(data.juryCount);
      if (data.juryWaitDeadline) {
        setJuryWaitDeadline(data.juryWaitDeadline);
      }
    });

    const unsubJuryLeft = wsService.on('JURY_LEFT', () => {
      setJuryCount((prev) => Math.max(0, prev - 1));
    });

    const unsubDebaterReady = wsService.on('DEBATER_READY', (data) => {
      setProReady(data.proReady);
      setConReady(data.conReady);
      // 양쪽 준비 완료 시 시작 데드라인 설정
      if (data.bothReady && data.startDeadline) {
        setStartDeadline(data.startDeadline);
      }
    });

    const unsubDebateStarting = wsService.on('DEBATE_STARTING', (data) => {
      setPhase('countdown');
      setTimeLeft(data.countdown || 3);
      // 서버에 countdown 시작 알림
      wsService.send({ action: 'CHANGE_PHASE', phase: 'countdown' });
    });

    // 페이즈 변경 동기화
    const unsubPhaseChanged = wsService.on('PHASE_CHANGED', (data) => {
      console.log('Phase changed:', data.phase);
      setPhase(data.phase as DebatePhase);
      if (PHASE_DURATIONS[data.phase]) {
        setTimeLeft(PHASE_DURATIONS[data.phase]);
      }
    });

    const unsubReaction = wsService.on('REACTION', (data) => {
      // 리액션 표시
      const reactionId = Date.now().toString();
      setRecentReactions(prev => [...prev, { id: reactionId, emoji: data.reaction, target: data.target }]);
      // 3초 후 제거
      setTimeout(() => {
        setRecentReactions(prev => prev.filter(r => r.id !== reactionId));
      }, 3000);
    });

    const unsubSupportUpdate = wsService.on('SUPPORT_UPDATE', (data) => {
      setSupportCounts({
        pro: data.support.pro,
        con: data.support.con,
        neutral: data.support.neutral,
      });
    });

    const unsubVoteUpdate = wsService.on('VOTE_UPDATE', (data) => {
      setTotalVotes(data.totalVotes);
    });

    const unsubVoteConfirmed = wsService.on('VOTE_CONFIRMED', (data) => {
      setMyVote(data.vote);
    });

    const unsubDebateEnded = wsService.on('DEBATE_ENDED', (data) => {
      console.log('DEBATE_ENDED received:', data);
      setPhase('result');
      setWinner(data.winner);
      if (data.votes) {
        setVotes(data.votes);
      }
      setResultCountdown(10);
    });

    const unsubError = wsService.on('ERROR', (data) => {
      console.error('Room error:', data);
    });

    return () => {
      unsubRejoined();
      unsubJoinedJury();
      unsubJoinedSpectator();
      unsubJuryJoined();
      unsubJuryLeft();
      unsubDebaterReady();
      unsubDebateStarting();
      unsubPhaseChanged();
      unsubReaction();
      unsubSupportUpdate();
      unsubVoteUpdate();
      unsubVoteConfirmed();
      unsubDebateEnded();
      unsubError();
    };
  }, []);

  // 배심원 대기 타이머
  useEffect(() => {
    if (phase !== 'preparing' || !juryWaitDeadline) return;

    const updateTimer = () => {
      const remaining = Math.max(0, Math.ceil((juryWaitDeadline - Date.now()) / 1000));
      setJuryWaitTimeLeft(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [phase, juryWaitDeadline]);

  // 양쪽 준비 후 시작 타이머
  useEffect(() => {
    if (phase !== 'preparing' || !startDeadline) return;

    const updateTimer = () => {
      const remaining = Math.max(0, Math.ceil((startDeadline - Date.now()) / 1000));
      setStartTimeLeft(remaining);
      
      // 타이머 만료 시 시작 요청
      if (remaining === 0) {
        wsService.send({ action: 'START_COUNTDOWN' });
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [phase, startDeadline]);

  // 토론 진행 타이머
  useEffect(() => {
    if (phase === 'preparing' || phase === 'result') return;

    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && PHASE_DURATIONS[phase]) {
      goToNextPhase();
    }
  }, [phase, timeLeft, goToNextPhase]);

  // 결과 화면 자동 로비 이동 타이머
  useEffect(() => {
    if (phase !== 'result') return;
    
    if (resultCountdown > 0) {
      const timer = setTimeout(() => setResultCountdown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      // 카운트다운 끝나면 전면 광고 표시
      setShowInterstitialAd(true);
    }
  }, [phase, resultCountdown]);

  // 전면 광고 시청 완료 후 로비로 이동
  const handleAdComplete = () => {
    setShowInterstitialAd(false);
    wsService.send({ action: 'LEAVE_ROOM' });
    navigate('/lobby', { replace: true });
  };

  // 핸들러들
  const handleReady = () => {
    wsService.send({ action: 'DEBATER_READY' });
  };

  const handleSendReaction = (reaction: string) => {
    if (!currentSpeaker) return;
    wsService.send({
      action: 'SEND_REACTION',
      reaction,
      target: currentSpeaker,
    });
  };

  const handleSupport = (side: Stance | null) => {
    if (role !== 'jury') return;
    setMySupport(side);
    wsService.send({
      action: 'JURY_SUPPORT',
      support: side,
    });
  };

  const handleVote = (vote: Stance | 'draw' | 'abstain') => {
    if (myVote) return;
    wsService.send({
      action: 'VOTE',
      vote,
    });
  };

  const handleLeave = () => {
    if (confirm('토론방을 나가시겠습니까?')) {
      wsService.send({ action: 'LEAVE_ROOM' });
      navigate('/lobby', { replace: true });
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getPhaseLabel = () => {
    switch (phase) {
      case 'preparing': return '⏳ 준비 중';
      case 'countdown': return '곧 시작!';
      case 'pro_main': return '🟢 찬성측 발언';
      case 'con_main': return '🔴 반대측 발언';
      case 'pro_rebuttal': return '🟢 찬성측 반론';
      case 'con_rebuttal': return '🔴 반대측 반론';
      case 'voting': return '⚖️ 투표 중';
      case 'result': return '결과 발표';
      default: return '';
    }
  };

  const getRoleLabel = () => {
    switch (role) {
      case 'debater': return `🎤 토론자 (${myStance === 'pro' ? '찬성' : '반대'})`;
      case 'jury': return '👨‍⚖️ 배심원';
      case 'spectator': return '👁️ 관전자';
      default: return '';
    }
  };

  const amIReady = role === 'debater' && myStance 
    ? (myStance === 'pro' ? proReady : conReady)
    : false;

  return (
    <>
      {/* 전면 광고 (게임 종료 후) - H5 Games Ads API 사용 */}
      <AdInterstitial
        isOpen={showInterstitialAd}
        onComplete={handleAdComplete}
        adType="next"
        placementName="debate-end"
      />
      
      <div className="min-h-screen bg-slate-900 flex flex-col">
        {/* Header */}
        <header className="p-3 flex items-center justify-between border-b border-slate-800">
          <button onClick={handleLeave} className="text-slate-400 hover:text-white text-sm">
            ✕ 나가기
          </button>
          <div className="text-center flex-1 px-2">
            <p className="text-xs text-slate-400">
              {getRoleLabel()} • 배심원 {juryCount}명
            </p>
          </div>
          <div className="w-16" />
        </header>

        {/* Topic */}
        {topic && (
          <div className="text-center py-2 px-4 bg-slate-800/50">
            <p className="text-sm truncate">{topic.emoji} {topic.title}</p>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col p-4 overflow-hidden">
        {/* 준비 화면 */}
        {phase === 'preparing' && (
          <div className="flex-1 flex flex-col items-center justify-center">
            <div className="text-6xl mb-4">⏳</div>
            <h2 className="text-xl font-semibold mb-2">토론 준비 중</h2>
            
            {/* 양쪽 준비 완료 후 시작 카운트다운 */}
            {proReady && conReady && startDeadline && startTimeLeft > 0 && (
              <div className="text-center mb-4 p-4 bg-primary-500/20 rounded-xl border border-primary-500">
                <p className="text-sm text-primary-300">🎉 양쪽 토론자 준비 완료!</p>
                <p className="text-4xl font-mono font-bold text-primary-400 mt-2">
                  {startTimeLeft}초
                </p>
                <p className="text-xs text-primary-300/70 mt-1">
                  후 토론이 시작됩니다
                </p>
              </div>
            )}

            {/* 배심원 대기 타이머 (양쪽 준비 전에만 표시) */}
            {!(proReady && conReady) && juryWaitDeadline && juryWaitTimeLeft > 0 && (
              <div className="text-center mb-4">
                <p className="text-sm text-slate-400">배심원 대기</p>
                <p className="text-3xl font-mono text-primary-400">
                  {juryWaitTimeLeft}초
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  새 배심원 참가 시 10초 연장
                </p>
              </div>
            )}

            {/* 현재 배심원 수 */}
            <div className="bg-slate-800 rounded-xl p-4 mb-4">
              <p className="text-sm text-slate-400">현재 배심원</p>
              <p className="text-3xl font-bold text-center">{juryCount}명</p>
            </div>

            {/* 토론자 준비 상태 */}
            <div className="flex items-center gap-8 mb-6">
              <div className="text-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 
                  ${proReady ? 'bg-green-500/30 border-green-500' : 'bg-slate-800 border-slate-600'}`}>
                  <span>{proReady ? '✓' : '👍'}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">찬성</p>
              </div>
              <div className="text-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 
                  ${conReady ? 'bg-red-500/30 border-red-500' : 'bg-slate-800 border-slate-600'}`}>
                  <span>{conReady ? '✓' : '👎'}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">반대</p>
              </div>
            </div>

            {/* 토론자 준비 버튼 */}
            {role === 'debater' && (
              <button
                onClick={handleReady}
                disabled={amIReady}
                className={`px-8 py-4 rounded-xl text-lg font-semibold transition-all ${
                  amIReady
                    ? 'bg-green-500/20 text-green-400 border-2 border-green-500'
                    : 'btn-primary'
                }`}
              >
                {amIReady ? '✓ 준비 완료' : '준비하기'}
              </button>
            )}

            {/* 배심원/관전자 안내 */}
            {role !== 'debater' && (
              <p className="text-slate-400 text-sm">
                토론자들이 준비 중입니다...
              </p>
            )}
          </div>
        )}

        {/* 카운트다운 */}
        {phase === 'countdown' && (
          <div className="flex-1 flex flex-col items-center justify-center">
            <p className="text-slate-400 mb-4">토론 시작</p>
            <span className="text-8xl font-bold text-primary-400 animate-pulse">
              {timeLeft}
            </span>
          </div>
        )}

        {/* 토론 진행 중 */}
        {(phase.includes('main') || phase.includes('rebuttal')) && (
          <>
            {/* Phase & Timer */}
            <div className="text-center mb-4">
              <p className="text-sm font-semibold">{getPhaseLabel()}</p>
              <div className="text-3xl font-mono font-bold text-primary-400">
                {formatTime(timeLeft)}
              </div>
            </div>

            {/* 실시간 지지 현황 */}
            {juryCount > 0 && (
              <div className="flex justify-center gap-4 mb-3 text-sm">
                <span className="text-green-400">👍 {supportCounts.pro}</span>
                <span className="text-slate-400">중립 {supportCounts.neutral}</span>
                <span className="text-red-400">👎 {supportCounts.con}</span>
              </div>
            )}

            {/* 발언권 표시 */}
            <div className="flex items-center justify-center gap-4 mb-4 relative">
              <div className={`px-4 py-2 rounded-full transition-all relative ${
                currentSpeaker === 'pro' 
                  ? 'bg-green-500/20 border border-green-500 scale-105' 
                  : 'bg-slate-800'
              }`}>
                👍 찬성 {role === 'debater' && myStance === 'pro' && '(나)'}
                {/* 찬성측 리액션 */}
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 flex gap-1">
                  {recentReactions.filter(r => r.target === 'pro').slice(-3).map(r => (
                    <span key={r.id} className="text-xl animate-bounce">{r.emoji}</span>
                  ))}
                </div>
              </div>
              <div className={`px-4 py-2 rounded-full transition-all relative ${
                currentSpeaker === 'con' 
                  ? 'bg-red-500/20 border border-red-500 scale-105' 
                  : 'bg-slate-800'
              }`}>
                👎 반대 {role === 'debater' && myStance === 'con' && '(나)'}
                {/* 반대측 리액션 */}
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 flex gap-1">
                  {recentReactions.filter(r => r.target === 'con').slice(-3).map(r => (
                    <span key={r.id} className="text-xl animate-bounce">{r.emoji}</span>
                  ))}
                </div>
              </div>
            </div>

            {/* 음성 토론 영역 */}
            <div className="flex-1 bg-slate-800/50 rounded-lg p-4 flex flex-col items-center justify-center mb-3">
              {/* 현재 발언자 표시 */}
              <div className="text-center mb-6">
                {currentSpeaker ? (
                  <>
                    <div className={`text-6xl mb-2 ${currentSpeaker === 'pro' ? 'animate-pulse' : 'animate-pulse'}`}>
                      {currentSpeaker === 'pro' ? '🎙️' : '🎙️'}
                    </div>
                    <p className={`text-xl font-bold ${
                      currentSpeaker === 'pro' ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {currentSpeaker === 'pro' ? '찬성측 발언 중' : '반대측 발언 중'}
                    </p>
                    {isMyTurn && (
                      <p className="text-yellow-400 mt-2 animate-pulse">
                        🎤 당신의 발언 시간입니다!
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-slate-400">대기 중...</p>
                )}
              </div>

              {/* 음성 상태 표시 (토론자) */}
              {role === 'debater' && (
                <div className="text-center">
                  {micError ? (
                    <p className="text-red-400 text-sm">{micError}</p>
                  ) : !isMicReady ? (
                    <p className="text-yellow-400 text-sm">마이크 연결 중...</p>
                  ) : (
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
                      isMuted ? 'bg-slate-700' : 'bg-green-500/30 border border-green-500'
                    }`}>
                      <span className={`w-3 h-3 rounded-full ${
                        isMuted ? 'bg-slate-500' : 'bg-green-500 animate-pulse'
                      }`} />
                      <span className="text-sm">
                        {isMuted ? '음소거' : '송출 중'}
                      </span>
                    </div>
                  )}
                  {isMyTurn && (
                    <p className="text-xs text-slate-400 mt-2">
                      발언 시간에 자동으로 마이크가 켜집니다
                    </p>
                  )}
                </div>
              )}

              {/* 음성 수신 상태 (배심원/관전자) */}
              {role !== 'debater' && (
                <div className="text-center">
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-700">
                    <span className="text-sm">🔊 청취 중</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-2">
                    연결된 피어: {connectedPeers}
                  </p>
                </div>
              )}
            </div>

            {/* 배심원 지지/리액션 버튼 */}
            {role === 'jury' && (
              <div className="space-y-3">
                <div className="flex justify-center gap-2">
                  <button
                    onClick={() => handleSupport(mySupport === 'pro' ? null : 'pro')}
                    className={`px-4 py-2 rounded-lg transition-all ${
                      mySupport === 'pro' 
                        ? 'bg-green-500 text-white' 
                        : 'bg-green-500/20 border border-green-500 hover:bg-green-500/30'
                    }`}
                  >
                    👍 찬성 지지
                  </button>
                  <button
                    onClick={() => handleSupport(mySupport === 'con' ? null : 'con')}
                    className={`px-4 py-2 rounded-lg transition-all ${
                      mySupport === 'con' 
                        ? 'bg-red-500 text-white' 
                        : 'bg-red-500/20 border border-red-500 hover:bg-red-500/30'
                    }`}
                  >
                    👎 반대 지지
                  </button>
                </div>
                
                <div className="flex justify-center gap-2">
                  {['👍', '🔥', '😂', '🤔'].map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => handleSendReaction(emoji)}
                      className="p-3 text-xl bg-slate-700 hover:bg-slate-600 rounded-xl transition-all active:scale-90"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* 관전자 안내 */}
            {role === 'spectator' && (
              <div className="text-center text-slate-400 text-sm py-2">
                👁️ 관전 중 - 다음 게임에 참여하려면 대기열에 참가하세요
              </div>
            )}
          </>
        )}

        {/* 투표 */}
        {phase === 'voting' && (
          <div className="flex-1 flex flex-col items-center justify-center">
            <p className="text-lg font-semibold mb-2">{getPhaseLabel()}</p>
            <p className="text-4xl font-mono text-primary-400 mb-6">{formatTime(timeLeft)}</p>
            
            {role === 'jury' && !myVote ? (
              <>
                <p className="text-slate-400 mb-4">승자를 선택하세요</p>
                <div className="flex flex-wrap justify-center gap-3">
                  <button 
                    onClick={() => handleVote('pro')} 
                    className="px-6 py-3 bg-green-500/20 border border-green-500 rounded-xl hover:bg-green-500/30"
                  >
                    👍 찬성
                  </button>
                  <button 
                    onClick={() => handleVote('draw')} 
                    className="px-6 py-3 bg-yellow-500/20 border border-yellow-500 rounded-xl hover:bg-yellow-500/30"
                  >
                    🤝 무승부
                  </button>
                  <button 
                    onClick={() => handleVote('con')} 
                    className="px-6 py-3 bg-red-500/20 border border-red-500 rounded-xl hover:bg-red-500/30"
                  >
                    👎 반대
                  </button>
                  <button 
                    onClick={() => handleVote('abstain')} 
                    className="px-6 py-3 bg-slate-700 border border-slate-600 rounded-xl hover:bg-slate-600"
                  >
                    🚫 기권
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center">
                <p className="text-slate-400">
                  {role === 'jury' ? '✓ 투표 완료!' : '배심원 투표 중...'}
                </p>
                <p className="text-sm text-slate-500 mt-2">
                  현재 {totalVotes}명 투표 완료
                </p>
              </div>
            )}
          </div>
        )}

        {/* 결과 */}
        {phase === 'result' && (
          <div className="flex-1 flex flex-col items-center justify-center">
            <span className="text-6xl">
              {winner === 'draw' ? '🤝' : '🏆'}
            </span>
            <h2 className="text-2xl font-bold mt-4">
              {winner === 'draw' ? '무승부!' : winner === 'pro' ? '찬성측 승리!' : '반대측 승리!'}
            </h2>
            
            <div className="flex justify-center gap-4 mt-4 text-lg">
              <span className="text-green-400">👍 {votes.pro}</span>
              <span className="text-yellow-400">🤝 {votes.draw}</span>
              <span className="text-red-400">👎 {votes.con}</span>
              <span className="text-slate-400">🚫 {votes.abstain}</span>
            </div>
            
            {role === 'debater' && myStance && (
              <p className="mt-4 text-lg">
                {winner === myStance ? (
                  <span className="text-green-400">🎉 축하합니다! +100 EXP</span>
                ) : winner === 'draw' ? (
                  <span className="text-yellow-400">무승부 +50 EXP</span>
                ) : (
                  <span className="text-slate-400">아쉬워요 +30 EXP</span>
                )}
              </p>
            )}

            <p className="text-slate-400 mt-6">
              {resultCountdown}초 후 로비로 이동합니다
            </p>
            
            <button
              onClick={() => {
                wsService.send({ action: 'LEAVE_ROOM' });
                navigate('/lobby', { replace: true });
              }}
              className="btn-primary mt-4"
            >
              지금 로비로 이동
            </button>
          </div>
        )}
      </main>
      </div>
    </>
  );
}
